import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface Video {
  id: number
  title: string
  description: string | null
  file_path: string
  file_url: string
  display_order: number
}

interface HowToStartVideosProps {
  pageType: 'home' | 'affiliate-landing' | 'shareholder-dashboard' | 'affiliate-dashboard'
  title?: string
  className?: string
}

export const HowToStartVideos: React.FC<HowToStartVideosProps> = ({
  pageType,
  title = 'How To Start',
  className = ''
}) => {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedVideo, setExpandedVideo] = useState<number | null>(null)

  useEffect(() => {
    fetchVideos()
  }, [pageType])

  const fetchVideos = async () => {
    try {
      setLoading(true)
      setError(null)

      // Build query based on page type
      let query = supabase
        .from('how_to_start_videos')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      // Add page-specific filter
      switch (pageType) {
        case 'home':
          query = query.eq('visible_on_home', true)
          break
        case 'affiliate-landing':
          query = query.eq('visible_on_affiliate_landing', true)
          break
        case 'shareholder-dashboard':
          query = query.eq('visible_on_shareholder_dashboard', true)
          break
        case 'affiliate-dashboard':
          query = query.eq('visible_on_affiliate_dashboard', true)
          break
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        console.error('Error fetching videos:', fetchError)
        setError('Failed to load training videos')
        return
      }

      setVideos(data || [])
    } catch (err) {
      console.error('Error in fetchVideos:', err)
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (video: Video) => {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = video.file_url
      link.download = `${video.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp4`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('Error downloading video:', err)
      alert('Failed to download video. Please try again.')
    }
  }

  const toggleVideo = (videoId: number) => {
    setExpandedVideo(expandedVideo === videoId ? null : videoId)
  }

  if (loading) {
    return (
      <div className={`how-to-start-section ${className}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading training videos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`how-to-start-section ${className}`}>
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchVideos} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (videos.length === 0) {
    return null // Don't show section if no videos available
  }

  return (
    <div className={`how-to-start-section ${className}`}>
      <h2 className="section-title">{title}</h2>
      <p className="section-subtitle">
        Watch these step-by-step tutorials to get started with Aureus Africa
      </p>

      <div className="videos-grid">
        {videos.map((video) => (
          <div key={video.id} className="video-card">
            <div className="video-header" onClick={() => toggleVideo(video.id)}>
              <h3 className="video-title">
                <span className="play-icon">▶</span>
                {video.title}
              </h3>
              <button className="expand-button">
                {expandedVideo === video.id ? '−' : '+'}
              </button>
            </div>

            {video.description && (
              <p className="video-description">{video.description}</p>
            )}

            {expandedVideo === video.id && (
              <div className="video-player-container">
                <video
                  controls
                  controlsList="nodownload"
                  className="video-player"
                  preload="metadata"
                >
                  <source src={video.file_url} type="video/mp4" />
                  <source src={video.file_url} type="video/webm" />
                  Your browser does not support the video tag.
                </video>

                <div className="video-actions">
                  <button
                    onClick={() => handleDownload(video)}
                    className="download-button"
                  >
                    <span className="download-icon">⬇</span>
                    Download Video
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

